import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import {
  Sun, Moon, Coffee, Briefcase, Users, BookOpen, Sparkle,
  Bolt, ArrowRight, ChevronRight,
} from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet } from '../lib/api';
import LogTimeSheet from './LogTimeSheet';
import ActivityDetail from './ActivityDetail';
import './Today.css';

// ----- icon picker -------------------------------------------------------
const ICONS = [
  { match: /(morning|ritual|sunrise|wake|meditat)/i, Icon: Sun },
  { match: /(lunch|coffee|reset|break|meal|eat)/i,   Icon: Coffee },
  { match: /(client|meet|call|partner|standup)/i,    Icon: Users },
  { match: /(deep|focus|strategy|build|code|design|deck)/i, Icon: Briefcase },
  { match: /(learn|study|read|book|gtm|product)/i,   Icon: BookOpen },
  { match: /(evening|review|reflect|night|journal)/i, Icon: Moon },
];
const pickIcon = (name = '') =>
  (ICONS.find((e) => e.match.test(name)) || { Icon: Sparkle }).Icon;

// ----- helpers -----------------------------------------------------------
const fmtMinutes = (m) => {
  if (!m && m !== 0) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const fmtSleep = (hours) => {
  if (hours == null) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
};

const fmtHours = (h) => {
  if (h == null) return '—';
  const rounded = Math.round(h * 10) / 10;
  if (rounded < 1) {
    const mins = Math.round(h * 60);
    return `${mins}m`;
  }
  return `${rounded}h`;
};

// ----- normalize today response — returns { timed, untimed } -------------
function normalizePlan(activities, plan) {
  const items = Array.isArray(plan)
    ? plan
    : (plan && Array.isArray(plan.items)) ? plan.items
    : (plan && Array.isArray(plan.activities)) ? plan.activities
    : [];
  if (!items.length) return { timed: [], untimed: [] };
  const byId = new Map((activities || []).map((a) => [a.activity_id, a]));
  const rows = items.map((p, i) => {
    const a = byId.get(p.activity_id) || {};
    const name = p.name || a.name || 'Untitled';
    const time = p.preferred_time || a.preferred_time || '';
    const minutes =
      p.target_minutes || p.actual_minutes || p.duration_minutes ||
      a.duration_minutes || a.target_minutes || 30;
    const desc = p.short_description || a.short_description || p.description || a.description || '';
    const priorityNum = (p.priority != null ? p.priority : a.priority);
    return {
      id: p.activity_id || a.activity_id || `plan-${i}`,
      time: time ? time.slice(0, 5) : '',
      name,
      desc,
      minutes,
      priorityNum: typeof priorityNum === 'number' ? priorityNum : null,
      priority: !!p.is_top_priority,
    };
  });
  const timed = rows
    .filter((r) => r.time)
    .sort((a, b) => a.time.localeCompare(b.time));
  const untimed = rows.filter((r) => !r.time);
  return { timed, untimed };
}

// ----- now-awareness helpers ---------------------------------------------
const rowStartMinutes = (row) => {
  if (!row.time) return null;
  const [hh, mm] = row.time.split(':').map(Number);
  return hh * 60 + mm;
};

const isNowRow = (row, nowMinutes) => {
  if (!row.time) return false;
  const start = rowStartMinutes(row);
  if (start == null) return false;
  const end = start + (row.minutes || 0);
  return nowMinutes >= start && nowMinutes < end;
};

const fmtHHMM = (date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

// Client-local YYYY-MM-DD. The Today page must pin its progress request to
// the user's *local* calendar date — log writers persist under this same
// string, so anchoring the GET to it keeps reads and writes on the same
// row near midnight in non-UTC timezones.
const todayLocalISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function Today({ userId, user }) {
  const navigate = useNavigate();
  const haptics = useHaptics();
  const [timed, setTimed] = useState([]);
  const [untimed, setUntimed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [loggedTotals, setLoggedTotals] = useState({});
  const [logSheetFor, setLogSheetFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const longPressTimer = useRef(null);
  const rowRefs = useRef({});

  // Clock — tick aligned to wall-clock minute boundary (no drift)
  useEffect(() => {
    let timeoutId;
    const tick = () => {
      setCurrentDate(new Date());
      const ms = 60_000 - (Date.now() % 60_000);
      timeoutId = setTimeout(tick, ms);
    };
    const initialMs = 60_000 - (Date.now() % 60_000);
    timeoutId = setTimeout(tick, initialMs);
    return () => clearTimeout(timeoutId);
  }, []);

  // Fetch activities + today plan + progress
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const [actsRes, planRes, progRes] = await Promise.allSettled([
          apiGet(`/api/activities/${userId}`),
          apiGet(`/api/today/${userId}`).catch(() => null),
          apiGet(`/api/progress/today/${userId}?date=${todayLocalISO()}`).catch(() => null),
        ]);
        const activities = actsRes.status === 'fulfilled' && actsRes.value?.ok
          ? await actsRes.value.json().catch(() => [])
          : [];
        let plan = [];
        if (planRes.status === 'fulfilled' && planRes.value?.ok) {
          plan = await planRes.value.json().catch(() => []);
        }
        const { timed: t, untimed: u } = normalizePlan(activities, plan);
        if (cancelled) return;
        setTimed(t);
        setUntimed(u);

        if (progRes.status === 'fulfilled' && progRes.value?.ok) {
          const data = await progRes.value.json().catch(() => null);
          if (!cancelled && data) {
            setProgress(data);
            // Pre-populate completed from API
            if (data.completed_activity_ids && data.completed_activity_ids.length) {
              setCompleted(new Set(data.completed_activity_ids));
            } else if (Array.isArray(data.logs)) {
              // Derive from logs: any activity_id logged today counts as done
              setCompleted(new Set(data.logs.map((l) => l.activity_id).filter(Boolean)));
            }
            // Build sum of today's logged minutes per activity
            if (Array.isArray(data.logs)) {
              const totals = {};
              data.logs.forEach((l) => {
                if (!l?.activity_id) return;
                const m = Number(l.actual_minutes) || 0;
                totals[l.activity_id] = (totals[l.activity_id] || 0) + m;
              });
              setLoggedTotals(totals);
            }
          }
        }
      } catch {
        if (!cancelled) { setTimed([]); setUntimed([]); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Lightweight refetch of today's progress (logs + totals).
  // Called when the tab becomes visible or another view dispatches
  // `hx:logs-changed`. Doesn't toggle isLoading — silent refresh.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const refetchProgress = async () => {
      try {
        const r = await apiGet(`/api/progress/today/${userId}?date=${todayLocalISO()}`);
        if (!r.ok) return;
        const data = await r.json().catch(() => null);
        if (cancelled || !data) return;
        setProgress(data);
        if (Array.isArray(data.logs)) {
          const totals = {};
          data.logs.forEach((l) => {
            if (!l?.activity_id) return;
            const m = Number(l.actual_minutes) || 0;
            totals[l.activity_id] = (totals[l.activity_id] || 0) + m;
          });
          setLoggedTotals(totals);
          // Refresh "completed" derivation too, so deletions clear the strikethrough.
          if (data.completed_activity_ids && data.completed_activity_ids.length) {
            setCompleted(new Set(data.completed_activity_ids));
          } else {
            setCompleted(new Set(data.logs.map((l) => l.activity_id).filter(Boolean)));
          }
        }
      } catch (_) { /* silent */ }
    };
    const onVis = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refetchProgress();
      }
    };
    const onLogsChanged = () => refetchProgress();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('hx:logs-changed', onLogsChanged);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('hx:logs-changed', onLogsChanged);
    };
  }, [userId]);

  // Scroll now-row into view ONCE after timed rows first arrive — don't fight user scroll on refetch
  const scrolledOnceRef = useRef(false);
  useEffect(() => {
    if (scrolledOnceRef.current) return;
    if (!timed.length) return;
    scrolledOnceRef.current = true;
    const nowMins = currentDate.getHours() * 60 + currentDate.getMinutes();
    const nowRow = timed.find((r) => isNowRow(r, nowMins));
    if (nowRow) {
      const el = rowRefs.current[nowRow.id];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [timed, currentDate]);

  // Priority row selection (timed rows only) — only promote when truly flagged
  const priorityIdx = useMemo(() => {
    if (!timed.length) return -1;
    const explicit = timed.findIndex((r) => r.priority);
    if (explicit >= 0) return explicit;
    const highestPriority = timed.reduce((best, r, i) => {
      if (r.priorityNum == null) return best;
      if (best == null) return i;
      return (r.priorityNum > timed[best].priorityNum) ? i : best;
    }, null);
    if (highestPriority != null && timed[highestPriority].priorityNum >= 8) return highestPriority;
    return -1;
  }, [timed]);

  const goFocus = (row) => {
    haptics.trigger('light');
    navigate('/app/focus', { state: { activityId: row?.id } });
  };

  const openDetail = (row) => {
    haptics.trigger('light');
    setDetailFor(row);
  };

  const beginPriority = () => {
    const row = timed[priorityIdx];
    haptics.trigger('medium');
    navigate('/app/focus', { state: { activityId: row?.id } });
  };

  const openLogSheet = (row) => {
    haptics.trigger('light');
    setLogSheetFor(row);
  };

  const handleLogged = (row, savedLog) => {
    if (!row || !savedLog) return;
    const m = Number(savedLog.actual_minutes) || 0;
    setLoggedTotals((prev) => {
      const newTotal = (prev[row.id] || 0) + m;
      const planned = row.minutes || 0;
      if (planned > 0 && newTotal >= planned) {
        setCompleted((c) => new Set([...c, row.id]));
      }
      return { ...prev, [row.id]: newTotal };
    });
    setFlashId(row.id);
    setTimeout(() => setFlashId(null), 220);
    haptics.trigger('success');
  };

  const startLongPress = (row) => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => openLogSheet(row), 600);
  };
  const cancelLongPress = () => clearTimeout(longPressTimer.current);

  // ----- time budget computation -----------------------------------------
  const sleepHours = user?.sleep_hours ?? 8;
  const eatingHours = (user?.eating_minutes ?? 60) / 60;
  const overheadHours = (user?.toilet_minutes ?? 30) / 60;
  const wakingHours = 24 - sleepHours;
  const usableHours = Math.max(0, wakingHours - eatingHours - overheadHours);
  const allRows = [...timed, ...untimed];
  const plannedHours = allRows.reduce((s, r) => s + (r.minutes || 0), 0) / 60;
  const loggedMinutes = Number.isFinite(progress?.logged_minutes) ? progress.logged_minutes
    : Number.isFinite(progress?.logged) ? progress.logged
    : (Array.isArray(progress?.logs)
        ? progress.logs.reduce((s, l) => s + (l.actual_minutes || 0), 0)
        : 0);
  const loggedHours = Math.max(0, loggedMinutes) / 60;
  const budgetPct = plannedHours > 0
    ? Math.min(100, Math.round((loggedHours / plannedHours) * 100))
    : 0;

  // ----- now-awareness ---------------------------------------------------
  const nowMins = currentDate.getHours() * 60 + currentDate.getMinutes();
  const nowRowId = timed.find((r) => isNowRow(r, nowMins))?.id ?? null;

  // "Up next": first timed row whose start time is in the future
  const upNextRow = !nowRowId
    ? timed.find((r) => {
        const start = rowStartMinutes(r);
        return start != null && start > nowMins;
      })
    : null;

  // ----- loading skeleton ------------------------------------------------
  if (isLoading) {
    return (
      <div className="page today-page">
        <PageHeader user={user} />
        <header className="today-title-block">
          <h1 className="h-display today-title">Today</h1>
        </header>
        <div className="today-skeleton" aria-busy="true" aria-label="Loading today">
          {[1, 2, 3].map((n) => (
            <div key={n} className="skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  // ----- empty state -----------------------------------------------------
  if (!userId || (timed.length === 0 && untimed.length === 0)) {
    return (
      <div className="page today-page">
        <PageHeader user={user} />
        <header className="today-title-block">
          <h1 className="h-display today-title">Today</h1>
        </header>
        <div className="today-empty">
          <div className="today-empty-icon" aria-hidden>
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="28" cy="28" r="27" fill="var(--gold-100)" stroke="var(--gold-400)" strokeWidth="1.5" />
              <circle cx="28" cy="28" r="9" fill="none" stroke="var(--gold-400)" strokeWidth="1.5" />
              {[0,45,90,135,180,225,270,315].map((deg) => {
                const rad = (deg * Math.PI) / 180;
                const x1 = 28 + 13 * Math.cos(rad);
                const y1 = 28 + 13 * Math.sin(rad);
                const x2 = 28 + 17 * Math.cos(rad);
                const y2 = 28 + 17 * Math.sin(rad);
                return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--gold-400)" strokeWidth="1.5" strokeLinecap="round" />;
              })}
            </svg>
          </div>
          <p className="h-2 today-empty-headline">Your day is open.</p>
          <p className="subtitle today-empty-sub">
            Add activities to see your schedule here.
          </p>
          <button
            type="button"
            className="btn-primary today-empty-cta"
            onClick={() => navigate('/app/plans')}
          >
            Open Plans
          </button>
          <p className="today-empty-alt">Or describe your day in Reflection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page today-page">
      <PageHeader user={user} />

      <header className="today-title-block">
        <h1 className="h-display today-title">Today</h1>
        <p className="subtitle today-subtitle">
          Focus on what matters. Everything else can wait.
        </p>
      </header>

      {/* Up next CTA — only when no current row and no priority promoted (priority IS the up-next visually) */}
      {upNextRow && !nowRowId && priorityIdx < 0 && (
        <div className="up-next-card">
          <div className="up-next-text">
            <span className="eyebrow up-next-eyebrow">UP NEXT</span>
            <span className="up-next-name">{upNextRow.name}</span>
            <span className="up-next-time mono">{upNextRow.time}</span>
          </div>
          <button
            type="button"
            className="up-next-open"
            onClick={() => goFocus(upNextRow)}
            aria-label={`Open ${upNextRow.name}`}
          >
            Open
            <ArrowRight size={14} color="currentColor" />
          </button>
        </div>
      )}

      {/* TIMED TIMELINE */}
      {timed.length > 0 && (
        <ol className="today-timeline stagger" aria-label="Today timeline">
          {timed.map((row, i) => {
            const isPriority = priorityIdx >= 0 && i === priorityIdx;
            const isNow = row.id === nowRowId;
            const logged = loggedTotals[row.id] || 0;
            const planned = row.minutes || 0;
            const met = planned > 0 && logged >= planned;
            const done = met || completed.has(row.id);
            const flash = flashId === row.id;
            // Show NOW indicator before this row if it's the now row (no i > 0 guard)
            const showNowIndicator = isNow;

            if (isPriority) {
              return (
                <li
                  key={row.id}
                  className="timeline-item priority-item"
                  ref={(el) => { rowRefs.current[row.id] = el; }}
                >
                  {showNowIndicator && (
                    <div className="now-indicator" aria-label="Current time">
                      <span className="now-indicator-dot" aria-hidden />
                      <span className="now-indicator-time mono">{fmtHHMM(currentDate)}</span>
                      <span className="now-indicator-line" aria-hidden />
                    </div>
                  )}
                  <article
                    className={`black-card priority-card${flash ? ' is-flash' : ''}${isNow ? ' is-now' : ''}${done ? ' is-done' : ''}`}
                    onContextMenu={(e) => { e.preventDefault(); openLogSheet(row); }}
                  >
                    <div className="priority-time-rail mono">{row.time}</div>
                    <div className="priority-body">
                      <div className="eyebrow-gold priority-eyebrow">
                        <span className="priority-dot" aria-hidden />
                        TOP PRIORITY
                      </div>
                      <h2 className="h-2 priority-title">
                        {row.name || 'Untitled activity'}
                        {logged > 0 && (
                          <span className={`logged-pill${met ? ' is-met' : ''}`}>
                            {planned > 0 ? `${logged} / ${planned} min` : `${logged} min`}
                          </span>
                        )}
                      </h2>
                      {row.desc && <p className="priority-desc">{row.desc}</p>}
                      <div className="priority-footer">
                        <span className="priority-meta mono">
                          <Bolt size={14} color="var(--gold-400)" />
                          {fmtMinutes(row.minutes)}
                        </span>
                        <div className="priority-actions">
                          <button
                            type="button"
                            className={`log-trigger-btn${met ? ' is-met' : ''}`}
                            onClick={() => openLogSheet(row)}
                            aria-label={`Log time for ${row.name}`}
                          >
                            Log
                          </button>
                          <button
                            type="button"
                            className="priority-begin"
                            onClick={beginPriority}
                            aria-label={`Begin ${row.name}`}
                          >
                            <ArrowRight size={18} color="var(--ink)" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                  {i < timed.length - 1 && <div className="rule timeline-rule" aria-hidden />}
                </li>
              );
            }

            return (
              <li
                key={row.id}
                className="timeline-item"
                ref={(el) => { rowRefs.current[row.id] = el; }}
              >
                {showNowIndicator && (
                  <div className="now-indicator" aria-label="Current time">
                    <span className="now-indicator-dot" aria-hidden />
                    <span className="now-indicator-time mono">{fmtHHMM(currentDate)}</span>
                    <span className="now-indicator-line" aria-hidden />
                  </div>
                )}
                <div className={`timeline-row-wrap${done ? ' is-done' : ''}`}>
                  <button
                    type="button"
                    className={`timeline-row${done ? ' is-done' : ''}${flash ? ' is-flash' : ''}${isNow ? ' is-now' : ''}`}
                    onClick={() => openDetail(row)}
                    onContextMenu={(e) => { e.preventDefault(); openLogSheet(row); }}
                    onTouchStart={() => startLongPress(row)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    onMouseDown={() => startLongPress(row)}
                    onMouseUp={cancelLongPress}
                    onMouseLeave={cancelLongPress}
                    aria-label={`${row.time} — ${row.name}${done ? ' (completed)' : ''}${isNow ? ' (in progress)' : ''}`}
                  >
                    <span className="row-time mono">{row.time}</span>
                    <span className="row-icon" aria-hidden>
                      {(() => { const RowIcon = pickIcon(row.name); return <RowIcon size={18} color="var(--gold-400)" />; })()}
                    </span>
                    <span className="row-content">
                      <span className="row-title">{row.name}</span>
                      {row.desc && <span className="row-desc">{row.desc}</span>}
                      {planned > 0 && (
                        <span className="row-progress" aria-hidden>
                          <span
                            className={`row-progress-bar${met ? ' is-met' : ''}`}
                            style={{ width: `${Math.min(100, Math.round((logged / planned) * 100))}%` }}
                          />
                        </span>
                      )}
                    </span>
                    <span className={`row-mins mono${met ? ' is-met' : ''}`}>
                      {planned > 0 ? `${logged}/${planned}m` : (logged > 0 ? `${logged}m` : fmtMinutes(row.minutes))}
                    </span>
                    <span className="row-chevron" aria-hidden>
                      <ChevronRight size={16} color="currentColor" />
                    </span>
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* UNTIMED ACTIVITIES */}
      {untimed.length > 0 && (
        <section className="untimed-section" aria-label="Untimed activities">
          <p className="eyebrow untimed-eyebrow">ANYTIME TODAY</p>
          <ul className="untimed-list">
            {untimed.map((row) => {
              const logged = loggedTotals[row.id] || 0;
              const planned = row.minutes || 0;
              const met = planned > 0 && logged >= planned;
              const done = met || completed.has(row.id);
              const flash = flashId === row.id;
              const RowIcon = pickIcon(row.name);
              return (
                <li key={row.id} className="untimed-item">
                  <button
                    type="button"
                    className={`untimed-row${done ? ' is-done' : ''}${flash ? ' is-flash' : ''}`}
                    onClick={() => openDetail(row)}
                    onContextMenu={(e) => { e.preventDefault(); openLogSheet(row); }}
                    onTouchStart={() => startLongPress(row)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    aria-label={`${row.name}${done ? ' (completed)' : ''}`}
                  >
                    <span className="untimed-icon" aria-hidden>
                      <RowIcon size={16} color="currentColor" />
                    </span>
                    <span className="untimed-name">{row.name}</span>
                    <span className={`row-mins mono${met ? ' is-met' : ''}`}>
                      {planned > 0 ? `${logged}/${planned}m` : (logged > 0 ? `${logged}m` : fmtMinutes(row.minutes))}
                    </span>
                    <span className="row-chevron" aria-hidden>
                      <ChevronRight size={16} color="currentColor" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* TIME BUDGET */}
      <section className="time-budget" aria-label="Time budget">
        <p className="eyebrow time-budget-eyebrow">TODAY'S BUDGET</p>
        <div className="time-budget-stats">
          <div className="budget-stat">
            <span className="budget-stat-value">{fmtHours(usableHours)}</span>
            <span className="eyebrow budget-stat-label">Usable</span>
          </div>
          <div className="budget-stat">
            <span className="budget-stat-value">{fmtHours(plannedHours)}</span>
            <span className="eyebrow budget-stat-label">Planned</span>
          </div>
          <div className="budget-stat">
            {budgetPct >= 100
              ? <span className="budget-stat-value budget-complete-label">Done</span>
              : <span className="budget-stat-value">{fmtHours(loggedHours)}</span>
            }
            <span className="eyebrow budget-stat-label">Logged</span>
          </div>
        </div>
        <div
          className="budget-progress"
          role="progressbar"
          aria-valuenow={budgetPct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`budget-progress-bar${budgetPct >= 100 ? ' is-complete' : ''}`}
            style={{ width: `${budgetPct}%` }}
          />
        </div>
        {budgetPct >= 100 && (
          <p className="budget-complete-note">Day complete</p>
        )}
        {user?.sleep_hours != null && budgetPct < 100 && (
          <p className="budget-sleep-note">Sleep protected: {fmtSleep(user.sleep_hours)}</p>
        )}
      </section>

      {(() => {
        const bs = progress?.body_state || null;
        const energy = bs?.energy ?? bs?.energy_pct ?? null;
        const sleep = bs?.sleep_hours ?? user?.sleep_hours ?? null;
        const load = bs?.load ?? null;
        if (energy == null && sleep == null && load == null) return null;
        return (
          <section className="body-state-strip" aria-label="Body state">
            {energy != null && (
              <div className="bs-cell">
                <span className="eyebrow bs-label">Energy</span>
                <span className="mono bs-value">{Math.round(energy)}%</span>
              </div>
            )}
            {sleep != null && (
              <div className="bs-cell">
                <span className="eyebrow bs-label">Sleep</span>
                <span className="mono bs-value">{fmtSleep(sleep)}</span>
              </div>
            )}
            {load != null && (
              <div className="bs-cell">
                <span className="eyebrow bs-label">Load</span>
                <span className="mono bs-value">{load}</span>
              </div>
            )}
          </section>
        );
      })()}

      <LogTimeSheet
        activity={logSheetFor}
        open={!!logSheetFor}
        onClose={() => setLogSheetFor(null)}
        onLogged={(saved) => handleLogged(logSheetFor, saved)}
        defaultMinutes={logSheetFor?.minutes}
        userId={userId}
      />

      <ActivityDetail
        open={!!detailFor}
        userId={userId}
        activityId={detailFor?.id}
        onClose={() => setDetailFor(null)}
        onSave={() => setDetailFor(null)}
      />
    </div>
  );
}
